/**
 * App shell - main layout with plugin slots
 */

import React, { ReactNode, useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ToastContainer } from '../UI/Toast';
import { MockRoleBanner } from '../RBAC/MockRoleBanner';
import { NavItem } from '../../types/plugins';

import { TraceKitConfig } from '../../types/config';

export interface AppShellProps {
  children: ReactNode;
  pluginSidebarItems?: NavItem[];
  pluginHeaderActions?: React.ComponentType<any>[];
  onNavigate?: (path: string) => void;
  currentPath?: string;
  defaultTenantId?: string;
  defaultProjectId?: string;
  config?: TraceKitConfig;
}

export function AppShell({
  children,
  pluginSidebarItems,
  pluginHeaderActions,
  onNavigate,
  currentPath,
  defaultTenantId,
  defaultProjectId,
  config,
}: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Expand sidebar when on home page, collapse on other pages
  useEffect(() => {
    if (currentPath === '/') {
      setSidebarCollapsed(false);
    } else {
      setSidebarCollapsed(true);
    }
  }, [currentPath]);

  // Collapse sidebar when clicking on main content area (but not on interactive elements)
  const handleMainClick = (e: React.MouseEvent<HTMLElement>) => {
    // Don't collapse if clicking on interactive elements
    const target = e.target as HTMLElement;
    const isInteractive = target.closest('button, a, input, select, textarea, [role="button"], [onclick]');
    
    if (!isInteractive && currentPath !== '/' && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        pluginItems={pluginSidebarItems}
        onNavigate={onNavigate}
        currentPath={currentPath}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        logo={config?.branding?.logoUrl || config?.branding?.logo}
        logoAlt={config?.branding?.logoAlt || config?.branding?.appName}
        platformTitle={config?.branding?.platformTitle || config?.branding?.appName}
        platformSubtitle={config?.branding?.platformSubtitle}
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          pluginActions={pluginHeaderActions}
          defaultTenantId={defaultTenantId}
          defaultProjectId={defaultProjectId}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
          onNavigate={onNavigate}
        />
        
        <MockRoleBanner />
        
        <main 
          className="flex-1 overflow-auto p-6"
          style={{
            backgroundColor: 'var(--color-background-default, rgb(249, 250, 251))'
          }}
          onClick={handleMainClick}
        >
          {children}
        </main>
      </div>
      
      <ToastContainer />
    </div>
  );
}

